import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllCampaigns } from '../services/campaignService';
import { duplicateCampaign } from '../scripts/duplicateCampaign';
import { Campaign } from '../types/campaign';
import '../styles/MainPage.css';

/**
 * Admin page for duplicating campaigns
 * Accessible at /campaign-duplicator
 */
const CampaignDuplicatorPage: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<string>('');
  const [newPriority, setNewPriority] = useState<number>(3);
  const [newTitle, setNewTitle] = useState<string>('');
  const [mainCategoryPopular, setMainCategoryPopular] = useState<boolean>(false);
  const [mainCategoryAnbefalet, setMainCategoryAnbefalet] = useState<boolean>(true);
  const [loading, setLoading] = useState(true);
  const [duplicating, setDuplicating] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    setLoading(true);
    const data = await getAllCampaigns();
    setCampaigns(data);
    setLoading(false);
  };

  const handleDuplicate = async () => {
    if (!selectedCampaign) {
      setMessage({ type: 'error', text: 'Vælg en kampagne at kopiere' });
      return;
    }

    if (!mainCategoryPopular && !mainCategoryAnbefalet) {
      setMessage({ type: 'error', text: 'Vælg mindst én hovedkategori (Populære eller Anbefalet)' });
      return;
    }

    setDuplicating(true);
    setMessage(null);

    try {
      const mainCategory: ('popular' | 'anbefalet')[] = [];
      if (mainCategoryPopular) mainCategory.push('popular');
      if (mainCategoryAnbefalet) mainCategory.push('anbefalet');

      const modifications: any = { mainCategory };
      if (newTitle) modifications.title = newTitle;

      const result = await duplicateCampaign(selectedCampaign, newPriority, modifications);

      if (result) {
        setMessage({ 
          type: 'success', 
          text: `✅ Kampagne kopieret! Firestore ID: ${result.firestoreDocId}` 
        });
        
        // Reload campaigns to show the new one
        await loadCampaigns();
        
        // Reset form
        setSelectedCampaign('');
        setNewTitle('');
        setNewPriority(3);
        setMainCategoryPopular(false);
        setMainCategoryAnbefalet(true);
      } else {
        setMessage({ type: 'error', text: '❌ Kunne ikke kopiere kampagne' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Fejl: ' + (error as Error).message });
    } finally {
      setDuplicating(false);
    }
  };

  const selectedCampaignData = campaigns.find(c => c.id === selectedCampaign);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={() => navigate('/')} style={styles.backButton}>
          ← Tilbage
        </button>
        <h1 style={styles.title}>Kopier Kampagne</h1>
      </div>

      {loading ? (
        <div style={styles.loading}>Indlæser kampagner...</div>
      ) : (
        <div style={styles.form}>
          {/* Select Campaign */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Vælg kampagne at kopiere:</label>
            <select 
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              style={styles.select}
            >
              <option value="">-- Vælg kampagne --</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.title} (Priority: {campaign.priority})
                </option>
              ))}
            </select>
          </div>

          {/* Preview */}
          {selectedCampaignData && (
            <div style={styles.preview}>
              <h3 style={styles.previewTitle}>Forhåndsvisning:</h3>
              <div style={styles.previewCard}>
                <img 
                  src={selectedCampaignData.imageUrl} 
                  alt={selectedCampaignData.title}
                  style={styles.previewImage}
                />
                <div>
                  <strong>{selectedCampaignData.title}</strong>
                  <p style={styles.previewText}>{selectedCampaignData.description}</p>
                  <p style={styles.previewText}>
                    <small>
                      Priority: {selectedCampaignData.priority} • {selectedCampaignData.category}
                      {selectedCampaignData.mainCategory && (
                        <> • Kategorier: {selectedCampaignData.mainCategory.join(', ')}</>
                      )}
                    </small>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Main Category Selection */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Vælg hovedkategori(er):
            </label>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input 
                  type="checkbox"
                  checked={mainCategoryPopular}
                  onChange={(e) => setMainCategoryPopular(e.target.checked)}
                  style={styles.checkbox}
                />
                <span>Populære</span>
              </label>
              <label style={styles.checkboxLabel}>
                <input 
                  type="checkbox"
                  checked={mainCategoryAnbefalet}
                  onChange={(e) => setMainCategoryAnbefalet(e.target.checked)}
                  style={styles.checkbox}
                />
                <span>Anbefalet</span>
              </label>
            </div>
            <small style={styles.hint}>
              ℹ️ Du kan vælge begge kategorier - kampagnen vil vises i begge sektioner
            </small>
          </div>

          {/* New Priority */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Priority (lavere tal = højere prioritet):
            </label>
            <input 
              type="number"
              value={newPriority}
              onChange={(e) => setNewPriority(parseInt(e.target.value) || 0)}
              min="0"
              style={styles.input}
            />
            <small style={styles.hint}>
              📍 Priority bestemmer rækkefølgen inden for hver sektion
            </small>
          </div>

          {/* New Title (Optional) */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Ny titel (valgfri - lad være tom for at beholde original):
            </label>
            <input 
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="F.eks. 'Vind gratis levering (Kopi)'"
              style={styles.input}
            />
          </div>

          {/* Duplicate Button */}
          <button 
            onClick={handleDuplicate}
            disabled={!selectedCampaign || duplicating}
            style={{
              ...styles.button,
              opacity: (!selectedCampaign || duplicating) ? 0.5 : 1,
              cursor: (!selectedCampaign || duplicating) ? 'not-allowed' : 'pointer'
            }}
          >
            {duplicating ? '🔄 Kopierer...' : '📋 Kopier Kampagne'}
          </button>

          {/* Message */}
          {message && (
            <div style={{
              ...styles.message,
              backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
              color: message.type === 'success' ? '#155724' : '#721c24',
              border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
            }}>
              {message.text}
            </div>
          )}

          {/* Campaign List */}
          <div style={styles.campaignList}>
            <h3 style={styles.listTitle}>Alle kampagner ({campaigns.length}):</h3>
            {campaigns.map((campaign) => (
              <div key={campaign.id} style={styles.campaignItem}>
                <span style={styles.priority}>{campaign.priority}</span>
                <span>{campaign.title}</span>
                <span style={styles.section}>
                  {campaign.mainCategory ? 
                    `(${campaign.mainCategory.join(', ')})` : 
                    '(No category)'
                  }
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
  },
  header: {
    marginBottom: '30px',
  },
  backButton: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    marginBottom: '10px',
    color: '#FA00FF',
  },
  title: {
    fontSize: '32px',
    margin: '0',
  },
  loading: {
    textAlign: 'center',
    padding: '50px',
    fontSize: '18px',
  },
  form: {
    backgroundColor: '#f9f9f9',
    padding: '30px',
    borderRadius: '10px',
  },
  formGroup: {
    marginBottom: '25px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    borderRadius: '5px',
    border: '1px solid #ddd',
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    borderRadius: '5px',
    border: '1px solid #ddd',
    boxSizing: 'border-box',
  },
  hint: {
    display: 'block',
    marginTop: '5px',
    color: '#666',
    fontSize: '13px',
  },
  preview: {
    marginBottom: '25px',
    padding: '15px',
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #ddd',
  },
  previewTitle: {
    margin: '0 0 10px 0',
    fontSize: '16px',
  },
  previewCard: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
  },
  previewImage: {
    width: '100px',
    height: '100px',
    objectFit: 'cover',
    borderRadius: '8px',
  },
  previewText: {
    margin: '5px 0',
    fontSize: '14px',
  },
  button: {
    width: '100%',
    padding: '15px',
    fontSize: '18px',
    fontWeight: 'bold',
    backgroundColor: '#FA00FF',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  message: {
    marginTop: '20px',
    padding: '15px',
    borderRadius: '5px',
    fontSize: '14px',
  },
  campaignList: {
    marginTop: '40px',
    paddingTop: '30px',
    borderTop: '2px solid #ddd',
  },
  listTitle: {
    marginBottom: '15px',
    fontSize: '18px',
  },
  campaignItem: {
    display: 'flex',
    gap: '10px',
    padding: '10px',
    backgroundColor: 'white',
    marginBottom: '8px',
    borderRadius: '5px',
    alignItems: 'center',
  },
  priority: {
    backgroundColor: '#FA00FF',
    color: 'white',
    padding: '5px 10px',
    borderRadius: '5px',
    fontWeight: 'bold',
    minWidth: '30px',
    textAlign: 'center',
  },
  section: {
    marginLeft: 'auto',
    fontSize: '12px',
    color: '#666',
  },
  checkboxGroup: {
    display: 'flex',
    gap: '20px',
    marginTop: '10px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
};

export default CampaignDuplicatorPage;

